import React, { useState, useEffect, useMemo } from 'react';
import {
  Landmark, Construction, ShieldAlert, Bus, Zap, Home,
  CheckCircle2, FileText, Camera, ChevronRight, Gavel,
  ShieldCheck, Scale, BookOpen, Database, Fingerprint,
  LockKeyhole, Terminal, ShieldX, Microscope, QrCode,
  Verified, AlertTriangle, Loader2, Copy, Check,
} from 'lucide-react';

import { AuditoriaNormativaService } from '../../services/AuditoriaNormativaService';
import { calcularHash } from '../../services/AssetPericialService';
import { laudoApi } from '../../services/laudoApi';
import { ETAPA, ORDEM_FATORES, FATORES_CONFIG, STATUS_REVISAO } from '../../constants/vistoria';

/**
 * ============================================================
 * COMPONENTE: VistoriaRegiaoResumo
 * ------------------------------------------------------------
 * Auditoria final + protocolo SHA-256.
 *   ✔ Lê evidências de `laudo[etapa].evidencias` (padrão novo)
 *   ✔ Usa `AuditoriaNormativaService.auditarLaudoCompleto`
 *   ✔ Hash SHA-256 REAL via crypto.subtle (não Math.random)
 *   ✔ Bloqueia protocolo se houver pendência
 *   ✔ Navegação via ETAPA (sem números mágicos)
 * VERSÃO: 2.0.0
 * ============================================================
 */

const ICONES_FATOR = {
  [ETAPA.F01_SERVICOS]:       Landmark,
  [ETAPA.F02_INFRA]:          Construction,
  [ETAPA.F03_SEGURANCA]:      ShieldAlert,
  [ETAPA.F04_ACESSIBILIDADE]: Bus,
  [ETAPA.F05_COMERCIO]:       Zap,
  [ETAPA.F06_OCUPACAO]:       Home,
};

const CORES_FATOR = {
  [ETAPA.F01_SERVICOS]:       'text-blue-600',
  [ETAPA.F02_INFRA]:          'text-emerald-600',
  [ETAPA.F03_SEGURANCA]:      'text-red-600',
  [ETAPA.F04_ACESSIBILIDADE]: 'text-orange-600',
  [ETAPA.F05_COMERCIO]:       'text-amber-600',
  [ETAPA.F06_OCUPACAO]:       'text-purple-600',
};

/* =========================
   COMPONENTE
========================= */

export default function VistoriaRegiaoResumo({
  laudo = {},
  setLaudo,
  setNavegacao,
  infoLaudo = { numero: 'MH-2026-0042', engenheiro: 'MARCUS FERRAZ' },
  onProtocolo,
}) {
  const [assinando, setAssinando] = useState(false);
  const [hashDigital, setHashDigital] = useState(null);
  const [erro, setErro] = useState(null);
  const [copiado, setCopiado] = useState(false);

  /* ---------- auditoria real ---------- */
  const auditoria = useMemo(
    () => AuditoriaNormativaService.auditarLaudoCompleto(laudo),
    [laudo]
  );

  const pendentes = 6 - auditoria.revisados;
  const isBloqueado = !auditoria.podeProtocolar;

  /* ---------- validar bloco de fator ---------- */
  const handleValidarBloco = (etapa) => {
    if (!setLaudo) {
      setErro('setLaudo não fornecido — não é possível validar.');
      return;
    }

    const novoLaudo = {
      ...laudo,
      [etapa]: {
        ...laudo[etapa],
        statusRevisao: STATUS_REVISAO.REVISADO,
        dataRevisao: new Date().toLocaleString('pt-BR'),
        revisor: infoLaudo.engenheiro,
      },
    };

    setLaudo(novoLaudo);
    setErro(null);
  };

  /* ---------- assinatura SHA-256 REAL ---------- */
  const processarAssinatura = async () => {
    if (isBloqueado) {
      setErro('Protocolo bloqueado: fatores pendentes.');
      return;
    }

    setAssinando(true);
    setErro(null);

    try {
      // 1. Constrói pacote canônico do laudo (ordem estável)
      const pacoteCanonico = {
        report_id: infoLaudo.numero,
        perito: infoLaudo.engenheiro,
        data_protocolo: new Date().toISOString(),
        versao_schema: '1.0.0',
        fatores: ORDEM_FATORES.map(etapa => ({
          etapa,
          codigo: FATORES_CONFIG[etapa].codigo,
          statusRevisao: laudo[etapa]?.statusRevisao || null,
          fatorValorizacao: laudo[etapa]?.fatorValorizacao || null,
          textoConsolidado: laudo[etapa]?.textoConsolidado || null,
          totalEvidencias: Object.keys(laudo[etapa]?.evidencias || {}).length,
          hashesEvidencias: Object.values(laudo[etapa]?.evidencias || {})
            .map(ev => ev?.hashIntegridade)
            .filter(Boolean),
        })),
      };

      // 2. Serializa em JSON estável (ordem de chaves fixa)
      const json = JSON.stringify(pacoteCanonico);

      // 3. Hash SHA-256 do pacote
      const encoder = new TextEncoder();
      const bytes = encoder.encode(json);
      const hash = await calcularHash(bytes.buffer);

      // 4. Persiste o laudo + hash via laudoApi
      await laudoApi.salvarModulo({
        ...pacoteCanonico,
        modulo: 'PROTOCOLO_FINAL',
        hash_protocolo: hash,
      });

      setHashDigital(hash);
    } catch (err) {
      if (err._enfileirado) {
        // offline — gera hash mesmo assim e sinaliza
        setErro('Sem conexão — protocolo salvo localmente. Será reenviado automaticamente.');
        // hash já foi gerado? não — reexecuta localmente
        try {
          const pacote = JSON.stringify({ report_id: infoLaudo.numero, fallback: true });
          const hash = await calcularHash(new TextEncoder().encode(pacote).buffer);
          setHashDigital(hash);
        } catch {
          setErro('Falha ao gerar hash — verifique HTTPS.');
        }
      } else {
        setErro(`Falha ao protocolar: ${err.message}`);
      }
    } finally {
      setAssinando(false);
    }
  };

  /* ---------- copiar hash para clipboard ---------- */
  const copiarHash = async () => {
    if (!hashDigital) return;
    try {
      await navigator.clipboard.writeText(hashDigital);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* silencioso */
    }
  };

  /* ---------- após protocolo, aguarda confirmação e redireciona ---------- */
  useEffect(() => {
    if (!hashDigital) return;
    const timer = setTimeout(() => {
      if (typeof onProtocolo === 'function') {
        onProtocolo({ laudo, hashDigital });
      } else if (typeof setNavegacao === 'function') {
        setNavegacao(ETAPA.DASHBOARD);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [hashDigital, laudo, onProtocolo, setNavegacao]);

  /* =========================
     RENDER
  ========================= */

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] p-12 pb-32 font-black italic uppercase select-none">

      {/* MARCA D'ÁGUA */}
      <div className="fixed inset-0 pointer-events-none flex items-center justify-center opacity-[0.03] text-[150px] z-0 text-[#2F353B] font-black italic select-none">
        MONTE HERMOM
      </div>

      <div className="max-w-6xl mx-auto relative z-10 space-y-16">

        {/* HEADER */}
        <header className="flex justify-between items-end border-b-[12px] border-[#2F353B] pb-12">
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-blue-600">
              <Microscope size={32} />
              <span className="text-[12px] tracking-[0.6em] text-slate-400">
                PGLP ASSET INTEGRITY SYSTEM
              </span>
            </div>
            <h1 className="text-[42px] text-[#2F353B] leading-none tracking-[0.1em]">
              RESUMO <span className="text-blue-600">AUDITORIA FINAL</span>
            </h1>
            <p className="text-[11px] tracking-[0.5em] text-slate-400 mt-2">
              NBR 14653-2:2011 • PROTOCOLO DE CONFORMIDADE 2026
            </p>
          </div>

          <div className="flex flex-col items-end gap-3">
            <div className="bg-[#2F353B] px-12 py-6 rounded-[2.5rem] border-b-8 border-blue-600 text-white shadow-2xl flex items-center gap-6">
              <Database size={24} className="text-blue-400" />
              <span className="text-[18px]">ID: {infoLaudo.numero}</span>
            </div>
            <span className="text-[10px] text-slate-400 tracking-widest mr-4">
              REVISOR: {infoLaudo.engenheiro}
            </span>
          </div>
        </header>

        {/* ERRO */}
        {erro && (
          <div className="bg-red-900/90 text-white p-6 rounded-[2rem] flex items-center gap-6">
            <ShieldX size={24} />
            <span className="text-[11px] tracking-widest">{erro}</span>
          </div>
        )}

        {/* LISTA DE FATORES */}
        <div className="grid grid-cols-1 gap-12">
          {ORDEM_FATORES.map(etapa => {
            const config = FATORES_CONFIG[etapa];
            const dadosFator = laudo[etapa] || {};
            const isRevisado = dadosFator.statusRevisao === STATUS_REVISAO.REVISADO;
            const Icone = ICONES_FATOR[etapa];
            const cor = CORES_FATOR[etapa];

            // Evidências conforme novo padrão: laudo[etapa].evidencias = { slot: { url, hashIntegridade, ... } }
            const evidencias = Object.values(dadosFator.evidencias || {});

            return (
              <section
                key={etapa}
                className={`group bg-white rounded-[4.5rem] border-4 transition-all duration-700 shadow-xl overflow-hidden ${
                  isRevisado ? 'border-green-600 shadow-green-100' : 'border-slate-100'
                }`}
              >
                <div className="flex flex-col lg:flex-row">

                  {/* LATERAL DE IDENTIFICAÇÃO */}
                  <div className={`lg:w-72 p-12 flex flex-col justify-between items-center text-center border-r-4 border-slate-50 ${
                    isRevisado ? 'bg-green-50' : 'bg-slate-50/50'
                  }`}>
                    <div className={`w-24 h-24 rounded-[3rem] flex items-center justify-center shadow-2xl transition-all duration-1000 ${
                      isRevisado ? 'bg-green-600 text-white rotate-[360deg] scale-110' : 'bg-[#2F353B] text-white'
                    }`}>
                      <Icone size={44} />
                    </div>
                    <div className="space-y-2 mt-8">
                      <span className={`text-[11px] tracking-widest uppercase leading-tight ${cor}`}>
                        {config.titulo}
                      </span>
                      <p className="text-[8px] text-slate-400 tracking-[0.3em]">
                        CÓDIGO NORM. {config.nbrItem}
                      </p>
                    </div>
                  </div>

                  {/* CORPO DO FATOR */}
                  <div className="flex-1 p-14 space-y-10">
                    <div className="flex justify-between items-center border-b-2 border-slate-50 pb-8">
                      <div className="flex items-center gap-4">
                        <Fingerprint size={20} className="text-blue-600" />
                        <span className="text-slate-400 text-[10px] tracking-[0.4em]">
                          MEMÓRIA DE CÁLCULO / REDAÇÃO
                        </span>
                      </div>

                      {!isRevisado ? (
                        <button
                          onClick={() => handleValidarBloco(etapa)}
                          className="px-12 py-5 bg-blue-600 text-white rounded-full text-[11px] hover:bg-[#2F353B] transition-all shadow-xl flex items-center gap-4 active:scale-95"
                        >
                          VALIDAR DADOS <ChevronRight size={18} />
                        </button>
                      ) : (
                        <div className="flex items-center gap-4 bg-green-600 text-white px-8 py-4 rounded-full text-[10px] shadow-lg">
                          <Verified size={18} /> SELO DE REVISÃO ATIVO
                        </div>
                      )}
                    </div>

                    {/* REDAÇÃO NORMATIVA */}
                    <div className="bg-slate-50/50 p-10 rounded-[3.5rem] text-[#2F353B] text-[16px] leading-[2.4] text-justify border border-slate-100 shadow-inner italic">
                      {dadosFator.textoConsolidado || 'STATUS: AGUARDANDO SINCRONIZAÇÃO DE DADOS DE CAMPO.'}
                    </div>

                    {/* JUSTIFICATIVA TÉCNICA */}
                    {dadosFator.justificativaDivergencia && (
                      <div className="bg-[#2F353B] p-10 rounded-[3.5rem] border-l-[20px] border-blue-600 flex gap-8 items-start shadow-2xl">
                        <Terminal size={28} className="text-blue-400 shrink-0 mt-2" />
                        <div className="space-y-3">
                          <h4 className="text-[10px] text-blue-400 tracking-[0.5em] uppercase">
                            Justificativa Pericial Adicional:
                          </h4>
                          <p className="text-white text-[15px] leading-relaxed tracking-wide font-medium">
                            "{dadosFator.justificativaDivergencia}"
                          </p>
                        </div>
                      </div>
                    )}

                    {/* EVIDÊNCIAS FOTOGRÁFICAS */}
                    {evidencias.length > 0 && (
                      <div className="space-y-6">
                        <div className="flex items-center gap-4">
                          <Camera size={18} className="text-slate-400" />
                          <span className="text-[9px] text-slate-400 tracking-[0.5em]">
                            ATIVOS FOTOGRÁFICOS VINCULADOS ({evidencias.length})
                          </span>
                        </div>
                        <div className="flex gap-6 overflow-x-auto pb-6">
                          {evidencias.map((ev, idx) => (
                            <div key={ev.hashIntegridade || idx} className="relative flex-shrink-0 group/img">
                              <div className="w-48 h-36 rounded-[2rem] overflow-hidden border-4 border-white shadow-xl bg-slate-100 relative">
                                <img
                                  src={ev.url}
                                  alt={ev.fileName || `Evidência ${idx + 1}`}
                                  className="w-full h-full object-cover grayscale group-hover/img:grayscale-0 transition-all duration-700"
                                />
                                <div className="absolute inset-0 bg-blue-600/10 opacity-0 group-hover/img:opacity-100 transition-opacity" />
                              </div>
                              <div className="absolute -bottom-3 -right-3 bg-white text-blue-600 p-3 rounded-full shadow-xl border-2 border-blue-50">
                                <ShieldCheck size={16} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </section>
            );
          })}
        </div>

        {/* INDICADORES GLOBAIS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white border-4 border-slate-100 rounded-[3rem] p-8 text-center">
            <span className="text-[9px] text-slate-400 tracking-widest block mb-2">Índice Global</span>
            <span className={`text-[42px] font-black italic ${auditoria.indiceGlobal >= 80 ? 'text-green-600' : 'text-amber-500'}`}>
              {auditoria.indiceGlobal}%
            </span>
          </div>
          <div className="bg-white border-4 border-slate-100 rounded-[3rem] p-8 text-center">
            <span className="text-[9px] text-slate-400 tracking-widest block mb-2">Fatores Revisados</span>
            <span className="text-[42px] font-black italic text-[#2F353B]">
              {auditoria.revisados} <span className="text-slate-300">/ {auditoria.totalFatores}</span>
            </span>
          </div>
          <div className="bg-white border-4 border-slate-100 rounded-[3rem] p-8 text-center">
            <span className="text-[9px] text-slate-400 tracking-widest block mb-2">Bloqueados</span>
            <span className={`text-[42px] font-black italic ${auditoria.bloqueados.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {auditoria.bloqueados.length}
            </span>
          </div>
        </section>

        {/* FOOTER DE PROTOCOLO */}
        <footer className="pt-32 pb-20 border-t-8 border-slate-100 mt-20 flex flex-col lg:flex-row justify-between items-center gap-16">
          <div className="flex flex-col space-y-6 max-w-md">
            <div className="flex items-center gap-8">
              <div className="w-14 h-14 bg-[#2F353B] rounded-[1rem] flex items-center justify-center shadow-2xl">
                <Gavel className="text-blue-500" size={30} />
              </div>
              <div className="space-y-1">
                <span className="text-[28px] text-[#2F353B] tracking-[0.6em]">MONTE HERMOM</span>
                <p className="text-blue-600 text-[10px] tracking-[0.8em]">ASSET INTEGRITY • 2026</p>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 leading-relaxed tracking-widest text-justify">
              O PROTOCOLO DESTE LAUDO É IMUTÁVEL E ASSEGURA A INTEGRIDADE DAS COORDENADAS E EVIDÊNCIAS
              COLETADAS EM CAMPO SOB AS REGRAS DA NBR 14653.
            </p>
          </div>

          <div className="relative group">
            <div className={`absolute -inset-6 bg-blue-600/20 rounded-[8rem] blur-2xl transition-all ${isBloqueado ? 'hidden' : 'animate-pulse'}`} />
            <button
              disabled={isBloqueado || assinando}
              onClick={processarAssinatura}
              className={`relative flex items-center gap-16 px-40 py-18 rounded-[8rem] shadow-2xl transition-all duration-700 text-[24px] tracking-[0.2em] border-b-[12px] active:translate-y-2 active:border-b-0 ${
                isBloqueado || assinando
                  ? 'bg-slate-100 border-slate-200 text-slate-300 cursor-not-allowed'
                  : 'bg-[#2F353B] border-blue-800 text-white hover:bg-blue-600 hover:scale-105'
              }`}
            >
              {isBloqueado ? (
                <>REVISÃO PENDENTE <LockKeyhole size={36} /></>
              ) : assinando ? (
                <><Loader2 size={36} className="animate-spin" /> SELANDO...</>
              ) : (
                <>PROTOCOLAR REPORT <ChevronRight size={40} /></>
              )}
            </button>
          </div>
        </footer>
      </div>

      {/* MONITOR DE BLOQUEIO */}
      {isBloqueado && (
        <div className="fixed bottom-12 left-12 z-[100] bg-[#2F353B] text-white px-12 py-10 rounded-[4rem] shadow-2xl flex items-center gap-10 border-l-[25px] border-amber-500 border border-white/10">
          <ShieldAlert size={50} className="text-amber-500 animate-pulse" />
          <div className="space-y-2">
            <span className="text-[16px] tracking-[0.3em]">INTEGRIDADE RETIDA</span>
            <div className="flex gap-4">
              <span className="text-[10px] text-slate-400 tracking-widest">
                VALIDE OS FATORES PENDENTES:
              </span>
              <span className="text-[10px] text-amber-500 tracking-[0.5em]">
                {pendentes} / 06
              </span>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE ASSINATURA */}
      {assinando && !hashDigital && (
        <div className="fixed inset-0 z-[500] bg-[#2F353B]/98 backdrop-blur-3xl flex items-center justify-center p-8">
          <div className="bg-white w-full max-w-2xl rounded-[6rem] shadow-2xl border-[15px] border-[#2F353B] p-20 space-y-12 text-center">
            <Loader2 size={100} className="text-blue-600 mx-auto animate-spin" />
            <div className="space-y-6">
              <h3 className="text-[32px] text-[#2F353B] tracking-[0.1em]">PROCESSANDO HASH SHA-256</h3>
              <p className="text-[10px] text-slate-400 tracking-[0.2em]">
                Gerando assinatura criptográfica do laudo...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE SUCESSO */}
      {hashDigital && (
        <div className="fixed inset-0 z-[500] bg-[#2F353B]/98 backdrop-blur-3xl flex items-center justify-center p-8">
          <div className="bg-white w-full max-w-2xl rounded-[6rem] shadow-2xl border-[15px] border-green-600 p-20 space-y-12 text-center">
            <QrCode size={80} className="text-green-600 mx-auto" />
            <div className="space-y-6">
              <h3 className="text-[28px] text-green-600 tracking-widest leading-none">
                LAUDO PROTOCOLADO COM SUCESSO
              </h3>
              <div className="bg-slate-50 p-8 rounded-[2rem] border-2 border-dashed border-slate-200 relative">
                <p className="text-[10px] text-slate-400 tracking-[0.5em] mb-4 uppercase">
                  HASH DE INTEGRIDADE SHA-256:
                </p>
                <code className="text-[13px] text-[#2F353B] font-mono break-all tracking-normal lowercase block">
                  {hashDigital}
                </code>
                <button
                  onClick={copiarHash}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white border-2 border-slate-200 hover:border-blue-600 flex items-center justify-center transition-all"
                  title="Copiar hash"
                >
                  {copiado ? <Check size={16} className="text-green-600" /> : <Copy size={16} className="text-slate-400" />}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 animate-pulse tracking-[0.3em]">
              REDIRECIONANDO PARA O DASHBOARD...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}